import React, {useMemo, useState} from 'react';
import moment from "moment";
import CustomerCell from "../common/table/customer-cell";
import { getFormattedDate} from "../../utils/timeUtils";
import ShippingIcon from "../../resources/icons/ShippingIcon";
import {useTranslation} from "react-i18next";
import {ChevronDownIcon, ClockIcon, DocumentTextIcon} from "@heroicons/react/outline";
import {hasPermission} from "../partials/restricted/permission-wrapper";
import {pg, pt} from "../../constants/permissions";
import {useSelector} from "react-redux";
import {getWebPathParam} from "../../utils/converter";
import {LOGISTICS, SHIPPING, SHIPPING_DETAIL} from "../../routes/endpoints";
import FileCell from "../common/table/file-cell";

function ActiveDocument({data, height}) {
  const { t } = useTranslation();
  const shippings = useMemo(() => data?.shippings ?? [], [data?.shippings]);
  const totalRows = shippings.length;
  const hasRows = totalRows > 0;
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  const groupedShippings = useMemo(() => {
    if (!hasRows) {
      return [];
    }

    const unscheduledLabel = t("dashboard.active_document.unscheduled_section");

    const buckets = shippings.reduce((acc, shipping) => {
      const departureMoment = shipping?.departure_date ? moment(shipping.departure_date, "YYYY-M-D HH:mm:ss") : null;
      const hasDepartureDate = departureMoment && departureMoment.isValid();
      const bucketKey = hasDepartureDate ? departureMoment.clone().startOf("week").format("YYYY-MM-DD") : "unscheduled";

      if (!acc[bucketKey]) {
        if (hasDepartureDate) {
          const startOfWeek = departureMoment.clone().startOf("week");
          const endOfWeek = departureMoment.clone().endOf("week");
          acc[bucketKey] = {
            key: bucketKey,
            label: `${startOfWeek.format("D MMMM YYYY")} - ${endOfWeek.format("D MMMM YYYY")}`,
            timestamp: startOfWeek.valueOf(),
            shippings: []
          };
        } else {
          acc[bucketKey] = {
            key: bucketKey,
            label: unscheduledLabel,
            timestamp: Number.NEGATIVE_INFINITY,
            shippings: []
          };
        }
      }

      acc[bucketKey].shippings.push(shipping);
      return acc;
    }, {});

    return Object.values(buckets).sort((a, b) => b.timestamp - a.timestamp);
  }, [hasRows, shippings, t]);

  const { totalDocuments, totalUploadedDocuments } = useMemo(() => {
    if (!hasRows) {
      return { totalDocuments: 0, totalUploadedDocuments: 0 };
    }

    return shippings.reduce(
      (acc, shipping) => {
        const total = shipping?.document_counter?.total ?? 0;
        const uploaded = shipping?.document_counter?.uploaded ?? 0;
        acc.totalDocuments += total;
        acc.totalUploadedDocuments += uploaded;
        return acc;
      },
      { totalDocuments: 0, totalUploadedDocuments: 0 }
    );
  }, [hasRows, shippings]);

  const totalPendingDocuments = Math.max(totalDocuments - totalUploadedDocuments, 0);
  const wrapperStyle = height ? { height: `${height}px` } : undefined;

  if (!hasRows) {
    const emptyStyle = height ? { minHeight: `${height}px` } : undefined;
    return (
      <div style={emptyStyle} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 px-6 py-10 text-center">
        <DocumentTextIcon
          className="h-24 w-24 text-indigo-600"
          aria-hidden="true"
        />
        <p className="mt-4 text-2xl font-extrabold text-gray-900 sm:text-2xl sm:tracking-tight lg:text-2xl">
          {t("dashboard.active_document.not_found_title")}
        </p>
        <p className="mx-auto mt-2 max-w-xl text-md text-gray-500">
          {t("dashboard.active_document.not_found_description")}
        </p>
      </div>
    );
  }

  return (
    <div style={wrapperStyle} className="flex h-full flex-col">
      <div className="grid sm:grid-cols-2">
        <SummaryCard
          variant="primary"
          label={t("dashboard.active_document.summary_shipments")}
          helper={t("dashboard.active_document.title")}
          value={numberFormatter.format(totalRows)}
          icon={<ShippingIcon className="h-14 w-14 text-white/80" />}
        />
        <SummaryCard
          label={t("dashboard.active_document.summary_documents")}
          helper={t("dashboard.active_document.progress_counter", {
            uploaded: numberFormatter.format(totalUploadedDocuments),
            total: numberFormatter.format(totalDocuments)
          })}
          value={numberFormatter.format(totalPendingDocuments)}
          icon={<DocumentTextIcon className="h-10 w-10 text-indigo-600" />}
        />
      </div>

      <nav className="flex-1 overflow-y-auto" aria-label="Document progress">
        {groupedShippings.map((group) => (
          <section key={group.key} className="relative bg-white/95">
            <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("dashboard.active_document.title")}
                </p>
                <h3 className="text-base font-semibold text-gray-900">
                  {group.label.charAt(0).toUpperCase() + group.label.slice(1)}
                </h3>
              </div>
              <p className="text-xs font-semibold text-indigo-600">
                {t("dashboard.active_document.week_summary", { count: group.shippings.length })}
              </p>
            </div>

            <ul className="list-none space-y-3">
              {group.shippings.map((item) => (
                <li key={item.id}>
                  <Row item={item} numberFormatter={numberFormatter} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
    </div>
  );
}

const Row = ({ item, numberFormatter }) => {
  const { permissions } = useSelector((s) => s.userCompany);
  const { companyType } = useSelector((s) => s.user);
  const [isActive, setActive] = useState(false);
  const { t } = useTranslation();
  const documentCounter = item?.document_counter ?? {};
  const totalDocuments = documentCounter.total ?? 0;
  const uploadedDocuments = documentCounter.uploaded ?? 0;
  const hasDocuments = (item?.documents?.length ?? 0) > 0;
  const collapsibleId = `active-document-${item.id}`;

  let url = '#0';

  const isLinked = hasPermission(
    permissions,
    pg.SHIPPING,
    pt.EDIT,
    item.customer.id,
    companyType,
    true
  );

  if(isLinked){
    url = getWebPathParam([LOGISTICS,SHIPPING, item.id, SHIPPING_DETAIL]);
  }

  return (
    <article className="bg-white/90 p-4 text-sm text-gray-700 border-b">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <a rel="noreferrer" href={url} target="_blank" className="block">
            <CustomerCell customer={item.customer} secondExtra={item.tracking} extra={item?.description ?? false} />
          </a>
        </div>

        <div className="flex w-full flex-col items-end gap-3 text-right sm:w-64">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
            <ClockIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            {item.departure_date ? getFormattedDate(item.departure_date) : t("dashboard.active_document.no_departure")}
          </span>

          {hasDocuments && (
            <button
              type="button"
              onClick={() => setActive(!isActive)}
              aria-expanded={isActive}
              aria-controls={collapsibleId}
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-50/80 px-3 py-1 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
               <span>
                {t("dashboard.active_document.progress_counter", {
                  uploaded: numberFormatter.format(uploadedDocuments),
                  total: numberFormatter.format(totalDocuments)
                })}
              </span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${isActive ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {isActive && hasDocuments && (
        <div id={collapsibleId} className="mt-4 space-y-2 rounded-lg bg-indigo-50/40 p-3">
          {item.documents.map((doc) => (
            <div key={doc?.id ?? doc?.document_type?.id ?? doc?.file_name} className="rounded-lg bg-white/95 p-3 shadow">
              <FileCell file={doc} />
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

const SummaryCard = ({ label, value, helper, icon, variant = "secondary" }) => {
  const baseClasses = "flex items-center justify-between px-5 py-4";
  const primaryClasses = "bg-gradient-to-r from-blue-1000 to-blue-1000 text-white shadow-indigo-500/30";
  const secondaryClasses = "border border-gray-100 bg-white text-gray-900";

  return (
    <div className={`${baseClasses} ${variant === "primary" ? primaryClasses : secondaryClasses}`}>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
          {label}
        </p>
        <p className="mt-1 text-3xl font-extrabold">
          {value}
        </p>
        {
          helper && (
            <p className={`mt-1 text-xs ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
              {helper}
            </p>
          )
        }
      </div>
      <div className={`${variant === "primary" ? "text-white" : "text-blue-1000"} flex-shrink-0`}>
        {icon}
      </div>
    </div>
  );
};

export default ActiveDocument;
